"use client";

import type { AppSurface } from "@magnus/contract/values";
import { createContext, type ReactNode, use, useMemo } from "react";
import type { AnyMagnusClientTool, MagnusClient } from "../index";
import type { ToolPolicy } from "./tool-policy";

export interface MagnusContextValue {
  /** Which built-in SDK tools this surface exposes. Defaults to `"all"`. */
  builtInTools: ToolPolicy;
  /** The Magnus client (auth-applied fetch + typed tRPC). */
  client: MagnusClient;
  /** Where the copilot is running, e.g. "standalone-web" or "embedded".
   *  Carries the provider prop's type so consumers (feedback submit) don't
   *  have to cast back into the contract enum. */
  surface: AppSurface;
  /** Freeform structured context about the host surface, sent on each turn. */
  surfaceContext?: Record<string, unknown>;
  /**
   * Instruction appendix for this surface, sent on each turn (plan 032 §B2).
   * Untrusted server-side: only honored for surfaces whose config opts in,
   * sanitized + length-capped, and subordinate to Magnus's identity/safety
   * rules. Keep it stable per chat; per-view data belongs in `surfaceContext`.
   */
  surfacePromptAppendix?: string;
  /** Copilot-registered client-side tools (execution + generative UI). */
  tools: AnyMagnusClientTool[];
  /**
   * The signed-in user's id, when known. Used by composer surfaces to group
   * "owned by me" vs "shared with me" (e.g. skills) without coupling the SDK
   * to a specific auth client.
   */
  viewerId?: string;
}

const MagnusContext = createContext<MagnusContextValue | null>(null);

export interface MagnusProviderProps {
  /**
   * Which built-in Magnus tools to expose on this surface. Defaults to `"all"`
   * (the full Magnus experience). Use `{ include }` for an allowlist or
   * `{ exclude }` for a denylist. See {@link ToolPolicy}.
   */
  builtInTools?: ToolPolicy;
  children: ReactNode;
  client: MagnusClient;
  /** Where the copilot is running. Validated server-side against
   *  `appSurfaceValues`; typed here so a typo fails at compile time. */
  surface: AppSurface;
  surfaceContext?: Record<string, unknown>;
  /** See {@link MagnusContextValue.surfacePromptAppendix}. Validated and
   *  gated server-side (`allowClientSurfacePrompt` per surface). */
  surfacePromptAppendix?: string;
  tools?: AnyMagnusClientTool[];
  /** The signed-in user's id, when known (see {@link MagnusContextValue.viewerId}). */
  viewerId?: string;
}

/**
 * Root provider for Magnus copilots. Supplies the client, the host surface,
 * and the registered tools to `useMagnus` / `useMagnusTools` /
 * `useToolRegistry` below it.
 */
export function MagnusProvider({
  client,
  surface,
  surfaceContext,
  surfacePromptAppendix,
  tools,
  builtInTools = "all",
  viewerId,
  children,
}: MagnusProviderProps) {
  const value = useMemo<MagnusContextValue>(
    () => ({
      client,
      surface,
      surfaceContext,
      surfacePromptAppendix,
      tools: tools ?? [],
      builtInTools,
      viewerId,
    }),
    [
      client,
      surface,
      surfaceContext,
      surfacePromptAppendix,
      tools,
      builtInTools,
      viewerId,
    ]
  );
  return (
    <MagnusContext.Provider value={value}>{children}</MagnusContext.Provider>
  );
}

export function useMagnusClient(): MagnusContextValue {
  const ctx = use(MagnusContext);
  if (!ctx) {
    throw new Error("useMagnusClient must be used within <MagnusProvider>");
  }
  return ctx;
}
